type ToastVariant = 'info' | 'success' | 'error';

interface ToastOptions {
  /** Icon template key to use as the left icon. Defaults to the variant. */
  icon?: string;
  /** Visual style. Default: 'info'. */
  variant?: ToastVariant;
  /** Auto-dismiss delay in seconds. 0 keeps the toast until manually closed. Takes precedence over `duration`. */
  seconds?: number;
  /** Auto-dismiss delay in ms. 0 keeps the toast until manually closed. Default: 5000. */
  duration?: number;
  /** Announce assertively (role="alert"). Defaults to true for the 'error' variant. */
  assertive?: boolean;
  /** Dismiss all existing toasts before showing this one. Default: false. */
  replace?: boolean;
}

interface TimerState {
  remaining: number;
  startedAt: number;
  timeoutId: number;
}

const DEFAULT_DURATION = 5000;
const MAX_TOASTS = 3;
const ANIMATION_MS = 250;
const VARIANTS: ToastVariant[] = ['info', 'success', 'error'];

class ToastRegion extends HTMLElement {
  private static triggerListenerAdded = false;

  private dismissLabel = 'Close';
  private iconTemplates = new Map<string, HTMLTemplateElement>();
  private closeTemplate: HTMLTemplateElement | null = null;
  private timers = new WeakMap<HTMLElement, TimerState>();

  connectedCallback(): void {
    this.dismissLabel = this.getAttribute('data-dismiss-label') || this.dismissLabel;

    this.querySelectorAll<HTMLTemplateElement>('template[data-toast-icon]').forEach((tpl) => {
      const key = tpl.getAttribute('data-toast-icon');
      if (key) this.iconTemplates.set(key, tpl);
    });
    this.closeTemplate = this.querySelector<HTMLTemplateElement>('template[data-toast-close]');

    window.showToast = (message: string, options?: ToastOptions): HTMLElement | undefined =>
      this.show(message, options);

    if (!ToastRegion.triggerListenerAdded) {
      document.addEventListener('click', ToastRegion.handleTriggerClick);
      ToastRegion.triggerListenerAdded = true;
    }
  }

  private static handleTriggerClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement | null;
    const trigger = target?.closest<HTMLElement>('[data-toast-trigger]');
    if (!trigger || typeof window.showToast !== 'function') return;

    const message = trigger.dataset.toastMessage;
    if (!message) return;

    const variant = trigger.dataset.toastVariant as ToastVariant | undefined;
    // data-toast-duration is expressed in seconds (0 = do not auto-close).
    const secondsAttr = trigger.dataset.toastDuration;
    const seconds = secondsAttr !== undefined && secondsAttr !== '' ? Number(secondsAttr) : undefined;
    window.showToast(message, {
      variant: variant && VARIANTS.includes(variant) ? variant : 'info',
      icon: trigger.dataset.toastIcon,
      duration: seconds !== undefined && !Number.isNaN(seconds) ? seconds * 1000 : undefined,
      replace: trigger.dataset.toastReplace !== undefined,
    });
  };

  show(message: string, options: ToastOptions = {}): HTMLElement | undefined {
    if (!message) return undefined;

    const variant: ToastVariant = options.variant && VARIANTS.includes(options.variant) ? options.variant : 'info';
    const assertive = options.assertive ?? variant === 'error';
    const duration =
      options.seconds !== undefined && !Number.isNaN(options.seconds)
        ? Math.max(0, options.seconds) * 1000
        : (options.duration ?? DEFAULT_DURATION);
    const iconKey = options.icon ?? variant;

    if (options.replace) this.dismissAll(true);

    const toast = this.buildToast(message, variant, iconKey, assertive);
    this.appendChild(toast);

    // Remove the oldest toast if we exceed the cap.
    const toasts = this.getToasts();
    if (toasts.length > MAX_TOASTS) {
      this.dismiss(toasts[0]);
    }

    // Trigger the enter transition after the node is painted.
    window.requestAnimationFrame(() => {
      toast.setAttribute('data-visible', 'true');
    });

    if (duration > 0) {
      this.startTimer(toast, duration);
    }

    return toast;
  }

  private buildToast(message: string, variant: ToastVariant, iconKey: string, assertive: boolean): HTMLElement {
    const toast = document.createElement('div');
    toast.className = `toast toast--${variant}`;
    toast.setAttribute('role', assertive ? 'alert' : 'status');
    toast.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
    toast.setAttribute('aria-atomic', 'true');

    const iconTemplate = this.iconTemplates.get(iconKey) ?? this.iconTemplates.get(variant);
    if (iconTemplate) {
      const icon = document.createElement('span');
      icon.className = 'toast__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.appendChild(iconTemplate.content.cloneNode(true));
      toast.appendChild(icon);
    }

    const text = document.createElement('span');
    text.className = 'toast__message';
    text.textContent = message;
    toast.appendChild(text);

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'toast__close';
    close.setAttribute('aria-label', this.dismissLabel);
    if (this.closeTemplate) {
      close.appendChild(this.closeTemplate.content.cloneNode(true));
    }
    close.addEventListener('click', () => this.dismiss(toast));
    toast.appendChild(close);

    // Pause auto-dismiss while the user is reading or interacting.
    toast.addEventListener('mouseenter', () => this.pauseTimer(toast));
    toast.addEventListener('mouseleave', () => this.resumeTimer(toast));
    toast.addEventListener('focusin', () => this.pauseTimer(toast));
    toast.addEventListener('focusout', () => this.resumeTimer(toast));

    return toast;
  }

  private getToasts(): HTMLElement[] {
    return Array.from(this.querySelectorAll<HTMLElement>('.toast'));
  }

  private dismissAll(immediate = false): void {
    this.getToasts().forEach((toast) => this.dismiss(toast, immediate));
  }

  private startTimer(toast: HTMLElement, remaining: number): void {
    const timeoutId = window.setTimeout(() => this.dismiss(toast), remaining);
    this.timers.set(toast, { remaining, startedAt: Date.now(), timeoutId });
  }

  private pauseTimer(toast: HTMLElement): void {
    const state = this.timers.get(toast);
    if (!state) return;
    window.clearTimeout(state.timeoutId);
    const elapsed = Date.now() - state.startedAt;
    state.remaining = Math.max(0, state.remaining - elapsed);
  }

  private resumeTimer(toast: HTMLElement): void {
    const state = this.timers.get(toast);
    if (!state || !toast.isConnected) return;
    this.startTimer(toast, state.remaining);
  }

  private dismiss(toast: HTMLElement, immediate = false): void {
    const state = this.timers.get(toast);
    if (state) {
      window.clearTimeout(state.timeoutId);
      this.timers.delete(toast);
    }

    if (immediate || this.prefersReducedMotion()) {
      toast.remove();
      return;
    }

    toast.setAttribute('data-visible', 'false');
    window.setTimeout(() => toast.remove(), ANIMATION_MS);
  }

  private prefersReducedMotion(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }
}

if (!customElements.get('toast-region')) {
  customElements.define('toast-region', ToastRegion);
}

export default ToastRegion;
export type { ToastOptions, ToastVariant };
